import { useState, useEffect, useCallback } from 'react'
import { Outlet, NavLink } from 'react-router-dom'
import {
  LayoutDashboard,
  Settings,
  BookMarked,
  Lightbulb,
  Network,
  Users,
  FileEdit,
  Zap,
  GitBranch,
  GitFork,
  Newspaper,
  ClipboardList,
  FlaskConical,
  Shield,
  Brain,
  MessageSquare,
  Headphones,
  Mic,
} from 'lucide-react'
import { clsx } from 'clsx'
import { ChatPanel } from './chat/ChatPanel'
import { ChatToggleButton } from './chat/ChatToggleButton'
import { ThemeToggle } from './ThemeToggle'

type NavItem = {
  to: string
  icon: React.ComponentType<{ className?: string }>
  label: string
}

const navItems: NavItem[] = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/notebook', icon: BookMarked, label: 'Notebook' },
  { to: '/solutions', icon: Lightbulb, label: 'Solutions' },
  { to: '/council', icon: Users, label: 'Council' },
  { to: '/knowledge-graph', icon: Network, label: 'KG Browser' },
  { to: '/mind-map', icon: GitFork, label: 'Mind Map' },
  { to: '/skills', icon: Zap, label: 'Skills' },
  { to: '/tracker', icon: GitBranch, label: 'Tracker' },
  { to: '/briefings', icon: Newspaper, label: 'Briefings' },
  { to: '/punch-list', icon: ClipboardList, label: 'Punch List' },
  { to: '/beta-feedback', icon: MessageSquare, label: 'Beta Feedback' },
  { to: '/tech-evals', icon: FlaskConical, label: 'Tech Evals' },
  { to: '/audiobooks', icon: Headphones, label: 'Audiobooks' },
  { to: '/voice-cloning', icon: Mic, label: 'Voice Cloning' },
  { to: '/safe-review', icon: Shield, label: 'SAFE Review' },
  { to: '/consciousness', icon: Brain, label: 'Consciousness' },
  { to: '/settings', icon: Settings, label: 'Settings' },
]

export function Layout() {
  const [chatOpen, setChatOpen] = useState(false)

  const toggleChat = useCallback(() => {
    setChatOpen(prev => !prev)
  }, [])

  // Cmd+K (Mac) / Ctrl+K (Windows) to toggle chat panel
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        toggleChat()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [toggleChat])

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-primary-50/30 dark:from-surface-950 dark:via-surface-900 dark:to-primary-950/20 transition-colors duration-300">
      {/* Subtle background pattern */}
      <div className="fixed inset-0 pattern-dots pointer-events-none opacity-50" />

      {/* Header */}
      <header className="glass-header fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-gradient-to-br from-primary-400 to-primary-600 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/50 ring-1 ring-white/30">
                <FileEdit className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-display text-xl font-extrabold tracking-widest text-red-500 uppercase" style={{ textShadow: '0 0 12px rgba(239,68,68,0.6), 0 0 40px rgba(239,68,68,0.3)' }}>
                  DOCTRINE
                </h1>
                <p className="font-display text-[10px] font-medium tracking-widest text-lime-400 uppercase" style={{ textShadow: '0 0 8px rgba(163,230,53,0.4)' }}>
                  Document Orchestration & Content Runtime Intelligence Network Engine
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="font-display text-xs font-medium tracking-widest text-primary-400/70 uppercase hidden sm:block">
                Skills Are The UI
              </span>
              <ThemeToggle />
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="glass-nav fixed top-16 left-0 right-0 z-40">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  clsx(
                    'flex items-center gap-1.5 px-2.5 py-3 text-sm font-medium border-b-2 transition-all whitespace-nowrap',
                    isActive
                      ? 'text-primary-600 dark:text-primary-400 border-primary-500'
                      : 'text-gray-600 dark:text-gray-400 border-transparent hover:text-primary-500 dark:hover:text-primary-300 hover:border-primary-300/50'
                  )
                }
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </NavLink>
            ))}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-40 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Outlet />
        </div>
      </main>

      <ChatToggleButton isOpen={chatOpen} onClick={() => setChatOpen(!chatOpen)} />
      <ChatPanel isOpen={chatOpen} onClose={() => setChatOpen(false)} />
    </div>
  )
}
